import { Component } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  Observable,
  Subject,
  combineLatest,
  defer,
  from,
  merge,
  of,
  timer,
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  shareReplay,
  startWith,
  switchMap,
  take,
} from 'rxjs';

import { ToastrService } from 'ngx-toastr';
import moment from 'moment';
import { FeatureFlagService } from 'src/app/modules/feature-flags';
import { DashboardFiltersService } from 'src/app/modules/voice-ai/dashboard-filters/dashboard-filters.service';
import { DashboardWebSocketService } from 'src/app/modules/voice-ai/dashboard-websocket/dashboard-websocket.service';
import { ApiGatewayAnalyticsService } from '../../../../../services/api-gateway-analytics/api-gateway-analytics.service';
import { DumbledoreService } from '../../../../../services/dumbledore.service';
import { VoiceAIMetrics } from '../../../../../services/api-gateway-analytics/types';
import { BotModel, CorpModel, HierarchyElementModel } from '../../../../../models';
import { CorpsService, HierarchyElementsService } from '../../../../../services/firestore';
import { AuthService } from '../../../../../services/auth.service';
import { VAConversationRow } from '../../components/dashboard-table/dashboard-table.interface';

@Component({
  selector: 'app-priority-dashboard',
  templateUrl: './priority-dashboard.page.html',
  styleUrls: ['./priority-dashboard.page.scss'],
})
export class PriorityDashboardPage {
  pageSize: number = 25;

  public corpId$: Observable<string>;
  public intentsForFilter$: Observable<{ code: string; name: string }[]>;
  public context$: Observable<{ corp: CorpModel; hierarchyElement: HierarchyElementModel; botConfig: BotModel; botId: string; dealerCode: string }>;

  public metricsData$: Observable<VoiceAIMetrics>;
  public conversationsData$: Observable<{ conversations: VAConversationRow[]; totalCount: number }>;
  public workListCounts$: Observable<Record<string, number>>;

  private readonly conversationResolvedTrigger$ = new Subject<void>();
  private refreshConversations$!: Observable<Date | null>;

  constructor(
    private activatedRoute: ActivatedRoute,
    private dumbledoreService: DumbledoreService,
    private corpsService: CorpsService,
    private apiGatewayAnalyticsService: ApiGatewayAnalyticsService,
    private hierarchyElementsService: HierarchyElementsService,
    private toasterService: ToastrService,
    private featureFlag: FeatureFlagService,
    private authService: AuthService,
    public filtersService: DashboardFiltersService,
    private dashboardWebSocketService: DashboardWebSocketService,
  ) {
    const dealerContext$ = this.activatedRoute.parent!.paramMap.pipe(
      map(params => ({
        corpId: params.get('corp')!,
        hierarchyElementSystemName: params.get('hierarchyElementSystemName')!,
      })),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    this.corpId$ = dealerContext$.pipe(
      map(context => context.corpId),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    const agentName$ = from(this.authService.getCurrentUserProfile()).pipe(
      map(user => `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim()),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    const intents$ = this.apiGatewayAnalyticsService.consolidatedIntents$.pipe(
      map(consolidatedIntents => {
        const intents: { code: string; name: string }[] = [];
        const intentsForFilter: { code: string; name: string }[] = [{ code: '', name: 'All' }];

        consolidatedIntents.forEach(intent => {
          intents.push({ code: intent.intent_code, name: intent.intent_description });

          if (intent.voiceai_enabled) {
            intentsForFilter.push({ code: intent.intent_code, name: intent.intent_description });
          }
        });

        return { intents, intentsForFilter };
      }),
      catchError(error => {
        console.error('Error loading intents', error);
        return [{ intents: [] as { code: string; name: string }[], intentsForFilter: [{ code: '', name: 'All' }] }];
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    this.intentsForFilter$ = intents$.pipe(
      map(data => data.intentsForFilter),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    this.context$ = dealerContext$.pipe(
      switchMap(({ corpId, hierarchyElementSystemName }) => {
        const dealerCode = `${corpId}-${hierarchyElementSystemName}`;

        return combineLatest({
          corp: this.corpsService.getCorpById(corpId),
          hierarchyElement: this.hierarchyElementsService.getHierarchyElement(dealerCode),
          botConfig: from(this.dumbledoreService.getBotConfigurationByDealershipCode(dealerCode)),
        }).pipe(
          map(({ corp, hierarchyElement, botConfig }) => {
            const botId = botConfig?._id;
            if (!botId) {
              this.toasterService.error(`Bot configuration not found for dealer code ${dealerCode}`, 'Error');
              throw new Error(`Bot configuration not found for dealer code ${dealerCode}`);
            }
            return { corp: corp as CorpModel, hierarchyElement: hierarchyElement as HierarchyElementModel, botConfig, dealerCode, botId };
          }),
          catchError(error => {
            console.error('Error loading context', error);
            throw error;
          }),
        );
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    const wsRefresh$ = this.context$.pipe(
      switchMap(({ botId }) => this.dashboardWebSocketService.connect(botId, this.filtersService.filters$)),
      debounceTime(2000),
    );

    this.refreshConversations$ = merge(this.conversationResolvedTrigger$.pipe(switchMap(() => timer(1000, 1500).pipe(take(3)))), wsRefresh$).pipe(
      map(() => new Date()),
      startWith(null),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    this.metricsData$ = combineLatest({
      botId: this.context$.pipe(map(ctx => ctx.botId)),
      filters: this.filtersService.filters$,
      refreshTrigger: this.refreshConversations$,
    }).pipe(
      distinctUntilChanged((prev, curr) => {
        if (prev.refreshTrigger !== curr.refreshTrigger && curr.refreshTrigger !== null) {
          return false;
        }

        return (
          prev.botId === curr.botId &&
          prev.filters.startDate.getTime() === curr.filters.startDate.getTime() &&
          prev.filters.endDate.getTime() === curr.filters.endDate.getTime()
        );
      }),
      switchMap(({ botId, filters: { startDate, endDate } }) =>
        defer(() => from(this.apiGatewayAnalyticsService.getVoiceAIMetrics(botId, this.formatStartUTC(startDate), this.formatEndUTC(endDate)))),
      ),
      catchError(error => {
        this.toasterService.error('Failed to load metrics', 'Error');
        console.error('Error loading metrics from analytics', error);
        return of({} as VoiceAIMetrics);
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    this.conversationsData$ = combineLatest({
      botId: this.context$.pipe(map(ctx => ctx.botId)),
      filters: this.filtersService.filters$,
      intents: intents$.pipe(map(i => i.intents)),
      refreshTrigger: this.refreshConversations$,
    }).pipe(
      distinctUntilChanged((prev, curr) => {
        if (prev.refreshTrigger !== curr.refreshTrigger && curr.refreshTrigger !== null) {
          return false;
        }

        return (
          prev.botId === curr.botId &&
          prev.filters.startDate.getTime() === curr.filters.startDate.getTime() &&
          prev.filters.endDate.getTime() === curr.filters.endDate.getTime() &&
          prev.filters.worklist === curr.filters.worklist &&
          prev.filters.intent === curr.filters.intent &&
          prev.filters.assignedTo === curr.filters.assignedTo &&
          prev.filters.search === curr.filters.search &&
          prev.filters.page === curr.filters.page &&
          prev.filters.sortBy === curr.filters.sortBy &&
          prev.filters.sortOrder === curr.filters.sortOrder &&
          prev.intents === curr.intents
        );
      }),
      switchMap(({ botId, filters, intents }) =>
        defer(() =>
          from(
            this.apiGatewayAnalyticsService.getVoiceAIConversations(
              botId,
              this.formatStartUTC(filters.startDate),
              this.formatEndUTC(filters.endDate),
              filters.worklist,
              filters.intent!,
              filters.assignedTo,
              filters.search!,
              filters.page,
              this.pageSize,
              filters.sortBy,
              filters.sortOrder,
            ),
          ).pipe(
            map(result => ({
              conversations: result.conversations.map(conv => ({
                ...conv,
                intentCodeName: intents.find(i => i.code === conv.intent) ?? { code: conv.intent, name: 'Unknown' },
                intentTooltip: intents.find(i => i.code === conv.last_intent_label)?.name ?? conv.last_intent_label ?? conv.intent,
              })),
              totalCount: result.totalCount,
            })),
          ),
        ).pipe(
          catchError(error => {
            this.toasterService.error('Failed to load conversations', 'Error');
            console.error('Error loading conversations from analytics', error);
            return of({ conversations: [], totalCount: 0 });
          }),
        ),
      ),
      shareReplay({ bufferSize: 1, refCount: true }),
    ) as Observable<{ conversations: VAConversationRow[]; totalCount: number }>;

    this.workListCounts$ = combineLatest({
      botId: this.context$.pipe(map(ctx => ctx.botId)),
      filters: this.filtersService.filters$,
      agentName: agentName$,
      refreshTrigger: this.refreshConversations$,
    }).pipe(
      distinctUntilChanged((prev, curr) => {
        if (prev.refreshTrigger !== curr.refreshTrigger && curr.refreshTrigger !== null) {
          return false;
        }

        return (
          prev.botId === curr.botId &&
          prev.filters.startDate.getTime() === curr.filters.startDate.getTime() &&
          prev.filters.endDate.getTime() === curr.filters.endDate.getTime() &&
          prev.filters.intent === curr.filters.intent &&
          prev.filters.assignedTo === curr.filters.assignedTo &&
          prev.filters.search === curr.filters.search &&
          prev.agentName === curr.agentName
        );
      }),
      switchMap(({ botId, filters, agentName }) =>
        defer(() => from(this.featureFlag.isEnabled('workListCountsFeatureFlag'))).pipe(
          switchMap(featureFlagEnabled => {
            if (!featureFlagEnabled) {
              return of({});
            }
            return from(
              this.apiGatewayAnalyticsService.getVoiceAICountByWorkList(
                botId,
                this.formatStartUTC(filters.startDate),
                this.formatEndUTC(filters.endDate),
                filters.intent!,
                filters.assignedTo || agentName,
                filters.search!,
              ),
            ).pipe(
              map(result =>
                result.reduce((acc, item) => {
                  acc[item.workListId] = item.count;
                  return acc;
                }, {} as Record<string, number>),
              ),
            );
          }),
        ),
      ),
      catchError(error => {
        this.toasterService.error('Failed to load conversations', 'Error');
        console.error('Error loading conversations from analytics', error);
        return of({});
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  public onDateChanged(dateRange: Partial<{ startDate: Date; endDate: Date }>): void {
    try {
      this.filtersService.updateFilters(dateRange);
    } catch (error) {
      this.toasterService.error('Starting date must be less than or equal to the ending date', 'Invalid Date Range');
    }
  }

  public onFiltersChanged({ searchTerm, intent, worklist, assignedTo, page, sortBy, sortOrder }) {
    this.filtersService.updateFilters({
      worklist,
      page,
      intent: intent || null,
      assignedTo: assignedTo || null,
      search: searchTerm || null,
      sortBy,
      sortOrder,
    });
  }

  public refreshConversations(): void {
    this.conversationResolvedTrigger$.next();
  }

  private formatStartUTC(date: Date): string {
    return moment(date).startOf('day').utc().toISOString();
  }

  private formatEndUTC(date: Date): string {
    return moment(date).endOf('day').utc().toISOString();
  }
}
